import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../utils/api';

/**
 * Shared hook for narrative/room management logic
 * Used by both mobile and desktop components
 */
export const useNarrativeRooms = () => {
  const [narratives, setNarratives] = useState([]);
  const [loadingNarratives, setLoadingNarratives] = useState(true);
  const [currentRoom, setCurrentRoom] = useState(null);

  // Memoized room creation function
  const createStableRoom = useCallback((apiRoom, narrative, isGuest = false, additionalProps = {}) => {
    return {
      ...apiRoom,
      narrative,
      isGuest,
      ...additionalProps
    };
  }, []);

  // Load narratives from v2 catalog API
  const loadNarratives = useCallback(async () => {
    try {
      setLoadingNarratives(true);
      const response = await api.getCatalog();

      if (response.catalog) {
        // Map v2 catalog fields to the shape the rest of the frontend expects
        const narratives = response.catalog.map(item => ({
          id: item.id,                      // v2 room_catalog_id
          title: item.name,
          description: item.description,
          image_url: item.thumbnail,
          max_participants: item.max_participants,
          genres: item.genres,
          genre: item.genres[0] || '',      // backwards-compat for existing components
          // room_catalog_id is the same as id — kept explicit for room creation
          room_catalog_id: item.id,
        }));
        setNarratives(narratives);
        return narratives;
      } else {
        console.error('❌ SHARED: getCatalog returned unexpected shape', response);
        return [];
      }
    } catch (error) {
      console.error('❌ SHARED: Error loading catalog:', error);
      setNarratives([]);
      return [];
    } finally {
      setLoadingNarratives(false);
    }
  }, []);

  // Create new room for narrative
  const createNewRoomForNarrative = useCallback(async (narrative) => {
    try {
      const response = await api.createRoom({
        room_catalog_id: narrative.room_catalog_id || narrative.id,
        is_private: false
      });

      if (response.success) {
        const stableRoom = createStableRoom(response.room, narrative);
        setCurrentRoom(stableRoom);
        return stableRoom;
      } else {
        console.error('❌ SHARED: Room creation failed:', response);
        throw new Error('Failed to create room');
      }
    } catch (error) {
      console.error('❌ SHARED: Room creation error:', error);
      throw error;
    }
  }, [createStableRoom]);

  // Join as guest
  const handleJoinAsGuest = useCallback(async (narrative) => {
    
    // Step 1: Get existing guest user from sessionStorage for reuse check
    const storedGuestUser = sessionStorage.getItem('guest_user');
    let existingGuestId = null;
    
    if (storedGuestUser) {
      try {
        const guestUser = JSON.parse(storedGuestUser);
        existingGuestId = guestUser.id;
        
        // Check for existing rooms with this guest user
        const roomsResponse = await api.getGuestRoomsForNarrative(narrative.id, guestUser.id);
        
        if (roomsResponse.success && roomsResponse.rooms.length > 0) {
          // Always show selection modal when rooms exist (even for 1 room)
          return { 
            hasMultipleRooms: true, 
            rooms: roomsResponse.rooms, 
            guestUser 
          };
        }
      } catch (error) {
        console.error('❌ SHARED: Error checking for existing guest rooms:', error);
        existingGuestId = null; // Reset if there was an error
      }
    }

    // Step 2: Use unified guest creation approach
    
    try {
      // NEW UNIFIED APPROACH - Get backend guest user first
      const guestResponse = await api.createOrGetGuestUser({
        narrative_id: narrative.id,
        existing_guest_id: existingGuestId
      });
      
      if (!guestResponse.success) {
        throw new Error('Failed to create/get unified guest user');
      }
      
      const guestUser = guestResponse.guest_user;
      const context = guestResponse.context;
      
      // Store backend-created guest user
      sessionStorage.setItem('guest_user', JSON.stringify(guestUser));
      
      // Step 3: Create room using unified guest room creation
      const roomResponse = await api.createGuestRoomUnified(guestUser.id, {
        room_catalog_id: narrative.room_catalog_id || narrative.id,
        is_private: false
      });

      if (roomResponse.success) {
        
        const stableRoom = createStableRoom(
          roomResponse.room,
          narrative,
          true,
          { guest_user_id: guestUser.id }
        );
        setCurrentRoom(stableRoom);
        return { room: stableRoom, hasMultipleRooms: false };
        
      } else {
        throw new Error('Failed to create room with unified guest user');
      }
      
    } catch (error) {
      console.error('❌ SHARED: Unified guest room creation error:', error);
      console.error('❌ SHARED: Error details:', {
        message: error.message,
        response: error.response,
        stack: error.stack
      });
      
      // Instead of falling back to broken old system, provide user-friendly error
      throw new Error(`Failed to create guest room: ${error.message}. Please try again.`);
    }
  }, [createStableRoom]);

  // Discover existing rooms for authenticated users
  const discoverUserRooms = useCallback(async (narrative, userId = null) => {
    try {
      
      // Get current user ID from parameter or fallback method
      const currentUserId = userId || getCurrentUserId();
      if (!currentUserId) {
        console.log('❌ SHARED: No user ID available for room discovery');
        const newRoom = await createNewRoomForNarrative(narrative);
        return { room: newRoom, hasMultipleRooms: false };
      }
      
      const response = await api.getUserRoomsForNarrative(currentUserId, narrative.id);
      
      if (response.success && response.rooms) {
        const existingRooms = response.rooms;
        
        if (existingRooms.length === 0) {
          // No existing rooms - create new one
          const newRoom = await createNewRoomForNarrative(narrative);
          return { room: newRoom, hasMultipleRooms: false };
        } else {
          // Always show selection modal when rooms exist (even for 1 room)
          return { hasMultipleRooms: true, rooms: existingRooms, narrative };
        }
      } else {
        console.log('⚠️ SHARED: No rooms found, creating new room');
        const newRoom = await createNewRoomForNarrative(narrative);
        return { room: newRoom, hasMultipleRooms: false };
      }
    } catch (error) {
      console.error('❌ SHARED: Room discovery error:', error);
      const newRoom = await createNewRoomForNarrative(narrative);
      return { room: newRoom, hasMultipleRooms: false };
    }
  }, [createNewRoomForNarrative, createStableRoom]);

  // Helper function to get current user ID
  const getCurrentUserId = () => {
    // This should be passed from the auth context or available globally
    // For now, try to get from localStorage or other sources
    const authUser = JSON.parse(localStorage.getItem('auth_user') || 'null');
    return authUser?.user_id || authUser?.id || null;
  };

  // Join a specific room (used when user selects from multiple rooms)
  const joinSpecificRoom = useCallback(async (room, narrative) => {
    try {
      const stableRoom = createStableRoom(room, narrative);
      setCurrentRoom(stableRoom);
      return stableRoom;
    } catch (error) {
      console.error('❌ SHARED: Error joining specific room:', error);
      throw error;
    }
  }, [createStableRoom, setCurrentRoom]);

  // Delete a room
  const deleteRoom = useCallback(async (roomId) => {
    try {
      const storedGuest = sessionStorage.getItem('guest_user');
      const guestId = storedGuest ? JSON.parse(storedGuest).id : null;
      const response = await api.deleteRoom(roomId, guestId);
      if (response.success) {
        return true;
      } else {
        throw new Error('Failed to delete room');
      }
    } catch (error) {
      console.error('❌ SHARED: Error deleting room:', error);
      throw error;
    }
  }, []);

  return {
    narratives,
    loadingNarratives,
    currentRoom,
    setCurrentRoom,
    loadNarratives,
    createStableRoom,
    createNewRoomForNarrative,
    handleJoinAsGuest,
    discoverUserRooms,
    joinSpecificRoom,
    deleteRoom
  };
};